import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { extractErrorMessage } from '@/lib/error-message'
import { formatPhone } from '@/lib/phone'
import { formatPropertyAddress } from '@/lib/property-address'
import {
	Alert, AlertTitle, AlertDescription,
	Avatar, AvatarFallback,
	Badge,
	Button,
	ConfirmDialog,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
	Input,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Spinner,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import { AlertCircleIcon, EllipsisVerticalIcon, SearchIcon } from 'lucide-react'
import {
	useDeleteSubAccountMutation,
	useGetOrganizationQuery,
	useListPropertiesQuery,
	useListSubAccountsQuery,
	useUpdateOrganizationSegmentMutation,
	useUpdateSubAccountMutation,
} from '@/api'
import {
	organizationSegmentLabel,
	subAccountStatusBadgeVariant,
	subAccountStatusLabel,
} from '@/lib/account-labels'
import { getInitials } from '@/lib/text-utils'
import { useMemberRoles, withHeldRoles } from '@/lib/use-member-roles'
import { AddEmployeeDialog } from '@/components/add-employee-dialog'
import { InviteSentDialog } from '@/components/invite-sent-dialog'
import { AssignedPropertiesDialog } from '@/components/assigned-properties-dialog'
import {
	ORGANIZATION_SEGMENTS,
	type OrganizationSegment,
	type SubAccount,
	type SubAccountRole,
	type SubAccountStatus,
} from '@/types'

export const Route = createFileRoute('/_app/settings/team')({
	component: SettingsTeamPage,
})

type FilterTab = 'all' | 'active' | 'invited' | 'blocked'

const TAB_TO_STATUS: Record<FilterTab, SubAccountStatus | undefined> = {
	all:     undefined,
	active:  'ACTIVE',
	invited: 'INVITED',
	blocked: 'BLOCKED',
}

function SettingsTeamPage() {
	const { t } = useTranslation()
	const [tab, setTab]             = useState<FilterTab>('all')
	const [roleFilter, setRoleFilter] = useState<SubAccountRole | 'all'>('all')
	const [propFilter, setPropFilter] = useState<string>('all')
	const [search, setSearch]       = useState('')
	const [addOpen, setAddOpen]     = useState(false)
	const [inviteSent, setInviteSent] = useState<{ fullName: string; email?: string; inviteLink?: string } | null>(null)
	const [assignedFor, setAssignedFor] = useState<SubAccount | null>(null)

	const { data: subAccounts, isLoading } = useListSubAccountsQuery({
		status:     TAB_TO_STATUS[tab],
		role:       roleFilter !== 'all' ? roleFilter : undefined,
		propertyId: propFilter !== 'all' ? propFilter : undefined,
		search:     search.trim() || undefined,
	})
	const { data: propertiesData } = useListPropertiesQuery({ limit: 100 })

	const [updateSub] = useUpdateSubAccountMutation()
	const [deleteSub] = useDeleteSubAccountMutation()

	// The segment decides which roles the team may hold; the server sends them.
	const { data: organization } = useGetOrganizationQuery()
	const [updateSegment, { isLoading: isUpdatingSegment }] = useUpdateOrganizationSegmentMutation()
	const roles = useMemberRoles()
	const [actionError, setActionError] = useState<{ title: string; description?: string } | null>(null)
	const [confirmBlock, setConfirmBlock] = useState<SubAccount | null>(null)
	const [confirmDelete, setConfirmDelete] = useState<SubAccount | null>(null)

	async function handleUpdateRole(id: string, role: SubAccountRole) {
		setActionError(null)
		try {
			await updateSub({ id, body: { role } }).unwrap()
		} catch (err) {
			setActionError({ title: t('settings.team.errors.updateRole'), description: extractErrorMessage(err) })
		}
	}

	async function handleChangeSegment(segment: OrganizationSegment) {
		setActionError(null)
		try {
			await updateSegment({ segment }).unwrap()
		} catch (err) {
			// The one refusal a valid choice can meet: a member still holds a
			// role the new segment does not offer.
			const message = extractErrorMessage(err)
			setActionError({
				title:       t('settings.team.errors.updateSegment'),
				description: message?.includes('does not offer') ? t('settings.team.errors.segmentRolesInUse') : message,
			})
		}
	}

	async function handleToggleBlock(sub: SubAccount) {
		const nextStatus = sub.status === 'BLOCKED' ? 'ACTIVE' : 'BLOCKED'
		setActionError(null)
		try {
			await updateSub({ id: sub.id, body: { status: nextStatus } }).unwrap()
		} catch (err) {
			setActionError({ title: t('settings.team.errors.updateStatus'), description: extractErrorMessage(err) })
		}
	}

	async function handleDelete(id: string) {
		setActionError(null)
		try {
			await deleteSub(id).unwrap()
		} catch (err) {
			setActionError({ title: t('settings.team.errors.delete'), description: extractErrorMessage(err) })
		}
	}

	const items = useMemo(() => subAccounts ?? [], [subAccounts])
	const properties = propertiesData?.properties ?? []

	// The filter lists the roles the segment offers and any role a member still
	// holds, so members who block a segment change can be found.
	const filterRoles = useMemo(() => withHeldRoles(roles, items), [roles, items])
	// A filter on a role that is no longer listed shows everyone instead of
	// an empty table with a blank filter.
	const activeRoleFilter = roleFilter !== 'all' && !filterRoles.some((r) => r.key === roleFilter) ? 'all' : roleFilter

	const tabs: { id: FilterTab; label: string }[] = [
		{ id: 'all',     label: t('settings.team.filters.all') },
		{ id: 'active',  label: t('settings.team.filters.active') },
		{ id: 'invited', label: t('settings.team.filters.invited') },
		{ id: 'blocked', label: t('settings.team.filters.blocked') },
	]

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase()
		return items.filter((sub) => {
			if (TAB_TO_STATUS[tab] && sub.status !== TAB_TO_STATUS[tab]) return false
			if (activeRoleFilter !== 'all' && sub.role !== activeRoleFilter) return false
			if (propFilter !== 'all' && !sub.properties.some((p) => p.propertyId === propFilter)) return false
			if (q && !sub.fullName.toLowerCase().includes(q) &&
				!(sub.email ?? '').toLowerCase().includes(q) &&
				!(sub.phone ?? '').toLowerCase().includes(q)) return false
			return true
		})
	}, [items, tab, activeRoleFilter, propFilter, search])

	return (
		<div className="flex h-full flex-col gap-4 px-4 py-4 overflow-hidden">
			{organization && (
				<div className="flex flex-wrap items-center gap-3">
					<span className="text-sm text-(--color-muted-foreground)">{t('settings.team.segment.label')}</span>
					<Select
						value={organization.segment}
						onValueChange={(v) => void handleChangeSegment(v as OrganizationSegment)}
						disabled={!organization.canUpdate || isUpdatingSegment}
					>
						{/* The trigger draws its disabled look from its own prop. */}
						<SelectTrigger className="w-60" disabled={!organization.canUpdate || isUpdatingSegment}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{ORGANIZATION_SEGMENTS.map((s) => (
								<SelectItem key={s} value={s}>{organizationSegmentLabel(t, s)}</SelectItem>
							))}
						</SelectContent>
					</Select>
					<span className="text-sm text-(--color-muted-foreground)">{t('settings.team.segment.agencyRolesHint')}</span>
				</div>
			)}

			<div className="flex flex-wrap items-center gap-3">
				<div className="flex flex-wrap gap-2">
					{tabs.map((f) => (
						<Button
							key={f.id}
							size="sm"
							variant={tab === f.id ? 'default' : 'secondary'}
							onClick={() => setTab(f.id)}
						>
							{f.label}
						</Button>
					))}
				</div>

				<Select value={activeRoleFilter} onValueChange={(v) => setRoleFilter(v as SubAccountRole | 'all')}>
					<SelectTrigger className="w-44">
						<SelectValue placeholder={t('settings.team.filters.role')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('settings.team.filters.allRoles')}</SelectItem>
						{filterRoles.map((r) => (
							<SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>
						))}
					</SelectContent>
				</Select>

				<Select value={propFilter} onValueChange={setPropFilter}>
					<SelectTrigger className="w-56">
						<SelectValue placeholder={t('settings.team.filters.object')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('settings.team.filters.allObjects')}</SelectItem>
						{properties.map((p) => (
							<SelectItem key={p.id} value={p.id}>
								{formatPropertyAddress(p.city, p.address)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				<div className="ml-auto flex items-center gap-3">
					<div className="relative">
						<SearchIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-muted-foreground)" />
						<Input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={t('settings.team.search')}
							className="h-9 w-56 pl-9"
						/>
					</div>
					<Button size="sm" onClick={() => setAddOpen(true)}>
						{t('settings.team.add')}
					</Button>
				</div>
			</div>

			{actionError && (
				<Alert variant="destructive">
					<AlertCircleIcon className="size-4" />
					<AlertTitle>{actionError.title}</AlertTitle>
					{actionError.description && <AlertDescription>{actionError.description}</AlertDescription>}
				</Alert>
			)}

			{isLoading ? (
				<Spinner size="sm" />
			) : filtered.length === 0 ? (
				<div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
					<p className="text-sm font-[500] text-(--color-foreground)">
						{items.length === 0
							? t('settings.team.empty')
							: t('settings.team.emptyFilter.title')}
					</p>
					<p className="text-sm text-(--color-muted-foreground)">
						{items.length === 0
							? t('settings.team.emptyHint')
							: t('settings.team.emptyFilter.hint')}
					</p>
					{items.length > 0 && (
						<Button variant="ghost" size="sm" className="mt-1" onClick={() => {
							setTab('all')
							setRoleFilter('all')
							setPropFilter('all')
							setSearch('')
						}}>
							{t('settings.team.emptyFilter.reset')}
						</Button>
					)}
				</div>
			) : (
				<div className="overflow-auto rounded-(--radius-lg) border border-(--color-border)">
					<Table>
						<TableHeader>
							<TableRow className="hover:bg-transparent">
								<TableHead className="px-4 font-[500] text-(--color-muted-foreground)">{t('settings.team.columns.employee')}</TableHead>
								<TableHead className="px-4 font-[500] text-(--color-muted-foreground)">{t('settings.team.columns.contacts')}</TableHead>
								<TableHead className="px-4 font-[500] text-(--color-muted-foreground)">{t('settings.team.columns.objects')}</TableHead>
								<TableHead className="px-4 font-[500] text-(--color-muted-foreground)">{t('settings.team.columns.role')}</TableHead>
								<TableHead className="px-4 font-[500] text-(--color-muted-foreground)">{t('settings.team.columns.status')}</TableHead>
								<TableHead className="w-[48px]" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{filtered.map((sub) => (
								<TableRow key={sub.id} className="h-16">
									<TableCell className="px-4">
										<div className="flex items-center gap-3">
											<Avatar>
												<AvatarFallback>{getInitials(sub.fullName)}</AvatarFallback>
											</Avatar>
											<span className="text-sm font-[500]">{sub.fullName}</span>
										</div>
									</TableCell>
									<TableCell className="px-4 text-sm text-(--color-muted-foreground)">
										<div className="flex flex-col gap-0.5">
											{sub.email && <span>{sub.email}</span>}
											{sub.phone && <span>{formatPhone(sub.phone)}</span>}
										</div>
									</TableCell>
									<TableCell className="px-4 text-sm text-(--color-muted-foreground)">
										<PropertyThumbs items={sub.properties} />
									</TableCell>
									<TableCell className="px-4">
										<Select
											value={sub.role}
											onValueChange={(v) => void handleUpdateRole(sub.id, v as SubAccountRole)}
										>
											<SelectTrigger className="w-36">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{withHeldRoles(roles, [sub]).map((r) => (
													<SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>
												))}
											</SelectContent>
										</Select>
									</TableCell>
									<TableCell className="px-4">
										<Badge variant={subAccountStatusBadgeVariant(sub.status)} size="sm">
											{subAccountStatusLabel(t, sub.status)}
										</Badge>
									</TableCell>
									<TableCell className="w-[48px] px-4">
										<DropdownMenu>
											<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label="Actions" />}>
												<EllipsisVerticalIcon className="size-4" />
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end" className="min-w-52">
												<DropdownMenuItem onClick={() => setAssignedFor(sub)}>
													{t('settings.team.actions.assignedObjects')}
												</DropdownMenuItem>
												<DropdownMenuItem
													onClick={() => {
														if (sub.status === 'BLOCKED') {
															void handleToggleBlock(sub)
														} else {
															setConfirmBlock(sub)
														}
													}}
												>
													{sub.status === 'BLOCKED'
														? t('settings.team.actions.unblock')
														: t('settings.team.actions.block')}
												</DropdownMenuItem>
												<DropdownMenuItem
													className="text-(--color-destructive) focus:text-(--color-destructive)"
													onClick={() => setConfirmDelete(sub)}
												>
													{t('settings.team.actions.delete')}
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			<AddEmployeeDialog
				open={addOpen}
				onOpenChange={setAddOpen}
				onSuccess={(info) => setInviteSent(info)}
			/>

			<InviteSentDialog
				open={inviteSent !== null}
				onOpenChange={(open) => { if (!open) setInviteSent(null) }}
				fullName={inviteSent?.fullName}
				email={inviteSent?.email}
				inviteLink={inviteSent?.inviteLink}
			/>
			<AssignedPropertiesDialog
				subAccount={assignedFor}
				open={assignedFor !== null}
				onOpenChange={(next) => { if (!next) setAssignedFor(null) }}
			/>

			<ConfirmDialog
				open={confirmBlock !== null}
				onOpenChange={(next) => { if (!next) setConfirmBlock(null) }}
				title={t('settings.team.blockDialog.title')}
				description={t('settings.team.blockDialog.description', { name: confirmBlock?.fullName ?? '' })}
				cancelLabel={t('common.cancel')}
				confirmLabel={t('settings.team.actions.block')}
				variant="destructive"
				onConfirm={() => {
					if (confirmBlock) void handleToggleBlock(confirmBlock)
					setConfirmBlock(null)
				}}
			/>

			<ConfirmDialog
				open={confirmDelete !== null}
				onOpenChange={(next) => { if (!next) setConfirmDelete(null) }}
				title={t('settings.team.deleteDialog.title')}
				description={t('settings.team.deleteDialog.description', { name: confirmDelete?.fullName ?? '' })}
				cancelLabel={t('common.cancel')}
				confirmLabel={t('settings.team.actions.delete')}
				variant="destructive"
				onConfirm={() => {
					if (confirmDelete) void handleDelete(confirmDelete.id)
					setConfirmDelete(null)
				}}
			/>
		</div>
	)
}

function PropertyThumbs({ items }: { items: SubAccount['properties'] }) {
	if (!items || items.length === 0) return <span>—</span>
	const visible = items.slice(0, 3)
	const rest    = items.length - visible.length
	return (
		<div className="flex items-center gap-1">
			{visible.map((p) => (
				<div key={p.propertyId} className="size-9 overflow-hidden rounded-(--radius-sm) bg-(--color-muted)">
					{p.imageUrl && <img src={p.imageUrl} alt="" className="size-full object-cover" />}
				</div>
			))}
			{rest > 0 && (
				<span className="flex h-9 items-center rounded-(--radius-sm) bg-(--color-muted) px-2 text-xs text-(--color-foreground)">
					+{rest}
				</span>
			)}
		</div>
	)
}
